import React, { useState, useEffect } from 'react';
import config from '../config';

const UserSetup = ({ onConnect, isConnecting, connectionError, initialUsername }) => {
  const [username, setUsername] = useState(initialUsername || '');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  const [authError, setAuthError] = useState('');
  const [retryAfter, setRetryAfter] = useState(0);

  useEffect(() => {
    if (retryAfter <= 0) return;
    const timer = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev <= 1) { setAuthError(''); return 0; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [retryAfter]);

  const getPasswordStrength = (pwd) => {
    if (pwd.length < config.minPasswordLength) return `Use at least ${config.minPasswordLength} characters.`;
    if (!/(?=.*[a-zA-Z])(?=.*\d)/.test(pwd)) return 'Add a mix of letters and numbers.';
    if (pwd.length >= 12 && /[!@#$%^&*]/.test(pwd)) return 'Strong password.';
    return pwd.length < 8 ? 'A longer password is even better.' : 'Good password.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim() || isConnecting || retryAfter > 0) return;
    setAuthError('');
    try {
      await onConnect(username.trim(), password.trim(), !isLogin);
    } catch (error) {
      if (error.retryAfterSeconds) setRetryAfter(error.retryAfterSeconds);
      setAuthError(error.message || 'Authentication failed');
    }
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value;
    if (/^[a-zA-Z0-9_-]*$/.test(value) && value.length <= config.maxUsernameLength) setUsername(value);
  };

  const isDisabled = isConnecting || retryAfter > 0;
  const isValidForm = username.trim().length >= config.minUsernameLength &&
    password.trim().length >= (isLogin ? 1 : config.minPasswordLength) && retryAfter === 0;
  const switchMode = (login) => { setIsLogin(login); setAuthError(''); setPassword(''); };
  const buttonText = retryAfter > 0 ? `Try again in ${retryAfter}s` : isConnecting ? 'Connecting…' : isLogin ? 'Sign in' : 'Create account';

  return (
    <div className="auth-layout">
      <section className="auth-intro" aria-labelledby="intro-title">
        <p className="eyebrow">A space to connect</p>
        <h2 id="intro-title">Good conversations.<br /><span>Simply connected.</span></h2>
        <p className="intro-description">Join the room. Catch up with someone.<br />Make a little space for conversation.</p>
        <div className="intro-note"><span className="note-line" aria-hidden="true" /><p>One shared room.<br />Or a conversation just for two.</p></div>
      </section>
      <section className="user-setup" aria-labelledby="auth-title">
        <div className="auth-switch" role="group" aria-label="Account access">
          <button type="button" aria-pressed={isLogin} onClick={() => switchMode(true)} disabled={isDisabled}>Sign in</button>
          <button type="button" aria-pressed={!isLogin} onClick={() => switchMode(false)} disabled={isDisabled}>Create account</button>
        </div>
        <h2 id="auth-title">{isLogin ? 'Welcome back.' : 'Make yourself at home.'}</h2>
        <p>{isLogin ? 'Your next conversation starts here.' : 'Choose a name. Start a conversation.'}</p>
        <form onSubmit={handleSubmit} className="user-setup-form" aria-label={isLogin ? 'Sign in to SecureChat' : 'Create your SecureChat account'}>
          <div className="form-field">
            <label htmlFor="chat-username">Username</label>
            <input id="chat-username" type="text" value={username} onChange={handleUsernameChange}
              placeholder="Your username" disabled={isDisabled} maxLength={config.maxUsernameLength}
              autoComplete="username" autoCapitalize="none" spellCheck={false} required aria-describedby="username-hint" />
            <small id="username-hint">{config.minUsernameLength}–{config.maxUsernameLength} characters. Letters, numbers, _ or -.</small>
          </div>
          <div className="form-field password-field">
            <label htmlFor="chat-password">Password</label>
            <input id="chat-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder={isLogin ? 'Enter your password' : 'Letters and numbers'} disabled={isDisabled}
              maxLength={config.maxPasswordLength} required autoComplete={isLogin ? 'current-password' : 'new-password'}
              aria-describedby={!isLogin ? 'password-hint' : undefined} />
            {!isLogin && <small id="password-hint" className="password-strength" aria-live="polite">{password ? getPasswordStrength(password) : `At least ${config.minPasswordLength} characters with letters and numbers.`}</small>}
          </div>
          {(connectionError || authError) && <div className="error-message" role="alert">{authError || connectionError}</div>}
          <button type="submit" className="btn-primary" disabled={!isValidForm || isConnecting} aria-busy={isConnecting}>{buttonText}<span aria-hidden="true">↗</span></button>
          <p className="auth-footnote">{isLogin ? 'Good to have you here again.' : 'Your username is how others will see you.'}</p>
        </form>
        <details className="security-info"><summary>About your account</summary><p>Passwords are hashed on the server. Sign-in attempts are rate-limited, with a temporary lockout after repeated failures.</p></details>
      </section>
    </div>
  );
};

export default UserSetup;
